// Menu-side audio (splash + home + map-select transition). Routes through
// the same THREE.js audio graph primitives the game uses (AudioBus + Music)
// so we get consistent volume semantics and a shared lowpass filter chain —
// previously splash/home music was an HTML <video> element (autoplay-muted
// trick) with manual DOM volume ramps, which was cross-browser fragile and
// couldn't be filtered.
//
// The menu owns its own AudioListener + AudioContext, independent of the
// game's (which lives in renderer.cameraRig). The two contexts coexist
// during the wipe: we fade menu music out here while the game's FxBus is
// constructing its own bus. Cheap, and keeps lifetimes cleanly separated.

import * as THREE from 'three';
import { CONFIG } from '../config';
import { AudioBus, Music } from '../render/GameAudio';

const MUSIC_URL = '/sounds/music-home.ogg';
const WHOOSH_URL = '/sounds/splash-to-home-whoosh.ogg';
const HOVER_URL = '/sounds/ui-hover.ogg';
const CLICK_URL = '/sounds/ui-click.ogg';
const CHANGING_ROOM_SWEEP_URL = '/sounds/ui-changing-room-sweep.ogg';
const MAP_SCREEN_SWEEP_URL = '/sounds/ui-map-screen-in-out-sweep.ogg';
const LEVEL_UP_URL = '/sounds/level-up.ogg';

// Fixed gains for UI one-shots, matching the previous DOM-audio values so the
// mix doesn't shift from the old implementation. Sweeps are louder because
// they're atmospheric transitions, not UI taps.
const HOVER_VOLUME = 0.6;
const CLICK_VOLUME = 0.8;
const SWEEP_VOLUME = 0.9;
const LEVEL_UP_VOLUME = 0.95;

// Voice pool size for hover/click. Small round-robin so rapid navigation
// (holding dpad, fast mouse sweep across menu items) can overlap cleanly
// without the audio graph recycling a still-playing voice.
const UI_POOL_SIZE = 4;

interface OneShotPool {
  voices: THREE.Audio[];
  buffer: AudioBuffer | null;
  next: number;
  volume: number;
}

class MenuAudioImpl {
  private readonly listener: THREE.AudioListener;
  private readonly scene: THREE.Scene;
  private readonly bus: AudioBus;
  private music: Music | null = null;
  private whoosh: THREE.Audio | null = null;
  /** Pooled one-shots for UI hover, click, and the two screen-transition
   *  sweeps. Round-robin so overlapping plays (fast menu navigation) don't
   *  cut each other off. Buffers are loaded lazily during preload; voices
   *  construct up-front so the pool doesn't allocate under pressure. */
  private hoverPool: OneShotPool | null = null;
  private clickPool: OneShotPool | null = null;
  private changingRoomSweepPool: OneShotPool | null = null;
  private mapScreenSweepPool: OneShotPool | null = null;
  private levelUpPool: OneShotPool | null = null;
  /** Per-source music level. The user-facing music volume slider lives on
   *  the bus's musicGain (see GameAudio.ts) — this number is the source's
   *  natural level (default 1.0) that transient fades ramp to/from for
   *  splash → home → match handoffs. */
  private currentMusicVol = 1.0;
  /** Monotonically bumped on each fadeMusic call so older in-flight fades
   *  self-cancel their next rAF step when a newer fade takes over. Prevents
   *  stale fade loops from stomping the volume they just finished setting. */
  private fadeToken = 0;
  private preloadPromise: Promise<void> | null = null;

  constructor() {
    // Dummy scene exists solely so AudioBus.anchor has a parent. The menu
    // has no positional sources, so nothing renders through this scene —
    // it's pure audio-graph scaffolding.
    this.listener = new THREE.AudioListener();
    this.scene = new THREE.Scene();
    this.scene.add(this.listener);
    this.bus = new AudioBus(this.listener, this.scene, /* reverb */ null);
  }

  /** Load music + whoosh buffers once. Idempotent — later calls return the
   *  same promise. Called from SplashScreen's setupPromise so audio is ready
   *  before the intro timeline starts. */
  preload(): Promise<void> {
    if (!this.preloadPromise) this.preloadPromise = this.doPreload();
    return this.preloadPromise;
  }

  private async doPreload(): Promise<void> {
    // Music construction kicks off its own async load; we don't await here
    // because Music.start() has a loaded-guard that defers play until the
    // buffer arrives. Everything else is awaited so UI SFX are ready when
    // the user hits a menu item (worst case ≈ the loading time of the whole
    // splash, which we're already blocking on).
    this.music = new Music(this.bus, MUSIC_URL, this.currentMusicVol);

    // Construct the UI pools up-front so later hover/click plays never
    // allocate. Buffers are filled in parallel with the whoosh load.
    this.hoverPool = this.makePool(UI_POOL_SIZE, HOVER_VOLUME);
    this.clickPool = this.makePool(UI_POOL_SIZE, CLICK_VOLUME);
    // Sweeps overlap themselves rarely (enter/exit-only) but keep 2 voices
    // so an abort-and-retry transition doesn't tail-cut.
    this.changingRoomSweepPool = this.makePool(2, SWEEP_VOLUME);
    this.mapScreenSweepPool = this.makePool(2, SWEEP_VOLUME);
    // Level-up is a one-at-a-time celebration; one voice is plenty.
    this.levelUpPool = this.makePool(1, LEVEL_UP_VOLUME);

    const loader = new THREE.AudioLoader();
    const load = (url: string): Promise<AudioBuffer> =>
      new Promise((resolve, reject) => loader.load(url, resolve, undefined, reject));

    const [whooshResult, hoverResult, clickResult, changingResult, mapResult, levelUpResult] = await Promise.allSettled([
      load(WHOOSH_URL),
      load(HOVER_URL),
      load(CLICK_URL),
      load(CHANGING_ROOM_SWEEP_URL),
      load(MAP_SCREEN_SWEEP_URL),
      load(LEVEL_UP_URL)
    ]);

    if (whooshResult.status === 'fulfilled') {
      const w = new THREE.Audio(this.listener);
      w.setBuffer(whooshResult.value);
      w.setLoop(false);
      w.setVolume(1.0);
      this.bus.registerNode(w);
      this.bus.attachSourceToSfx(w);
      this.whoosh = w;
    } else {
      console.warn('[MenuAudio] whoosh preload failed:', whooshResult.reason);
    }

    this.assignBuffer(this.hoverPool, hoverResult, 'ui-hover');
    this.assignBuffer(this.clickPool, clickResult, 'ui-click');
    this.assignBuffer(this.changingRoomSweepPool, changingResult, 'ui-changing-room-sweep');
    this.assignBuffer(this.mapScreenSweepPool, mapResult, 'ui-map-screen-in-out-sweep');
    this.assignBuffer(this.levelUpPool, levelUpResult, 'level-up');
  }

  /** Allocate a round-robin pool of non-positional THREE.Audio voices, all
   *  registered on the bus so the shared lowpass applies uniformly (SFX
   *  during the map-select sweep are dulled alongside music). */
  private makePool(size: number, volume: number): OneShotPool {
    const voices: THREE.Audio[] = [];
    for (let i = 0; i < size; i++) {
      const a = new THREE.Audio(this.listener);
      a.setLoop(false);
      a.setVolume(volume);
      this.bus.registerNode(a);
      // Menu hover/click/sweep are SFX from the user's perspective —
      // route through the SFX bus so the SFX volume slider applies.
      this.bus.attachSourceToSfx(a);
      voices.push(a);
    }
    return { voices, buffer: null, next: 0, volume };
  }

  private assignBuffer(pool: OneShotPool | null, result: PromiseSettledResult<AudioBuffer>, label: string): void {
    if (!pool) return;
    if (result.status === 'fulfilled') pool.buffer = result.value;
    else console.warn(`[MenuAudio] ${label} preload failed:`, result.reason);
  }

  private playPool(pool: OneShotPool | null): void {
    if (!pool || !pool.buffer) return;
    this.resume();
    const voice = pool.voices[pool.next]!;
    pool.next = (pool.next + 1) % pool.voices.length;
    if (voice.isPlaying) voice.stop();
    // setBuffer is cheap to re-apply (three.js just stores the ref). Keeps
    // each voice interchangeable across pools if we later share the array.
    voice.setBuffer(pool.buffer);
    voice.play();
  }

  /** Resume the AudioContext. Call from a user-gesture handler — Chromium
   *  won't actually resume until there's user activation, so invoking this
   *  off a capture-phase listener is the earliest reliable trigger. */
  resume(): void {
    this.bus.resumeIfSuspended();
  }

  /** Start home music looped. Safe to call before the AudioContext is
   *  resumed: the source schedules itself and begins on the next resume. */
  playMusic(): void {
    this.music?.start();
  }

  stopMusic(): void {
    this.music?.stop();
  }

  setMusicVolume(v: number): void {
    this.currentMusicVol = v;
    this.music?.setVolume(v);
  }

  /** rAF-driven linear volume ramp. Resolves on completion. When
   *  targetVolume is 0 the track is stopped at the end of the ramp — saves
   *  decoding work while the game owns the AudioContext output and is what
   *  the map-select → game hand-off wants. */
  fadeMusic(targetVolume: number, durationMs: number): Promise<void> {
    if (!this.music) return Promise.resolve();
    const token = ++this.fadeToken;
    const startV = this.currentMusicVol;
    const t0 = performance.now();
    return new Promise<void>((resolve) => {
      const step = (): void => {
        // A newer fade superseded us — bail without touching volume so we
        // don't overwrite the in-flight ramp.
        if (token !== this.fadeToken) { resolve(); return; }
        const t = Math.min(1, (performance.now() - t0) / durationMs);
        this.setMusicVolume(startV + (targetVolume - startV) * t);
        if (t >= 1) {
          if (targetVolume <= 0) this.stopMusic();
          resolve();
          return;
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  /** Fire the whoosh one-shot. Resumes the context on the caller's gesture
   *  (splash key-press), stops any in-flight voice so rapid re-triggers don't
   *  stack. */
  playWhoosh(): void {
    this.resume();
    const w = this.whoosh;
    if (!w) return;
    if (w.isPlaying) w.stop();
    w.play();
  }

  /** UI hover — menu focus changed. Pool-backed so back-to-back nav doesn't
   *  cut the previous hover short. */
  playHover(): void {
    this.playPool(this.hoverPool);
  }

  /** UI click — menu item activated (Enter, A button, mouse click). */
  playClick(): void {
    this.playPool(this.clickPool);
  }

  /** Changing-room enter + exit sweep. Fired on the transition in each
   *  direction; the underlying pan/animation is driven by HomeScreen. */
  playChangingRoomSweep(): void {
    this.playPool(this.changingRoomSweepPool);
  }

  /** Map-select enter + exit sweep. Fired on first reveal and again when the
   *  wipe-out begins (into the game). */
  playMapScreenSweep(): void {
    this.playPool(this.mapScreenSweepPool);
  }

  /** Level-up celebration sound. Fired by HomeScreen when the player returns
   *  to the lobby with a higher level than they had at last visit. */
  playLevelUp(): void {
    this.playPool(this.levelUpPool);
  }

  /** Smooth lowpass sweep on the shared filter bus. Used by MapSelectScreen
   *  to muffle home music as the game approaches — lands dulled just as the
   *  volume fade kicks in for the wipe. */
  sweepLowpass(cutoffHz: number, durationMs: number): void {
    this.bus.rampFilterCutoff(cutoffHz, durationMs / 1000);
  }

  /** Snap the filter back to bypass. Called on return-to-lobby so music
   *  resumes full-range after the game ends. */
  resetFilter(): void {
    this.bus.setFilterCutoff(AudioBus.BYPASS_HZ);
  }
}

export const menuAudio = new MenuAudioImpl();
